/**
 * HeroSection Component
 *
 * Stunning full-viewport hero with immersive visuals,
 * glassmorphism effects, and smooth animations.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Hero background images - beautiful Lebanon scenery
const HERO_IMAGES = [
  '/hero/pexels-bilel-naili-2794870-4335661.jpg', // Batroun coastline
  '/hero/hl-site-winter-sports-2-2000x1125.jpg',  // Lebanese ski resort
  '/hero/pexels-jokassis-4632796.jpg',             // Jounieh from Harissa
  '/hero/pexels-jokassis-5522722.jpg',             // Mountains above clouds
  '/hero/feat.jpg',                                 // Snow-capped mountains
  '/hero/pexels-the-trvlr-3793462.jpg',            // Ancient ruins
  '/hero/pexels-jokassis-4632800.jpg',             // Jounieh bay through trees
];

// Featured property cards for floating display (real Firebase Storage images)
const FLOATING_PROPERTIES = [
  { id: '2310', image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/listings/2310/cover.jpg', name: 'Al Abou Guesthouse', price: 80 },
  { id: '3938', image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/listings/3938/photo-1.jpg', name: 'Bonjourein Maison', price: 120 },
  { id: '1616', image: 'https://storage.googleapis.com/lebanest-e1a43.firebasestorage.app/listings/1616/cover.jpeg', name: 'Homesinn Cabin', price: 70 },
];

export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Cycle through background images
  useEffect(() => {
    setIsLoaded(true);
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative h-screen min-h-[700px] max-h-[1000px] overflow-hidden">
      {/* Animated Background Images */}
      {HERO_IMAGES.map((img, index) => (
        <div
          key={img}
          className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="w-full h-full bg-cover bg-center transform scale-105 animate-slow-zoom"
            style={{ backgroundImage: `url('${img}')` }}
          />
        </div>
      ))}

      {/* Multi-layer Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-900/30 via-transparent to-primary-900/30" />

      {/* Animated Particles/Stars Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/40 rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Floating Property Cards - Right Side, Sticky Note Style, Desktop Only */}
      <div className="hidden xl:flex flex-col gap-4 absolute right-6 2xl:right-10 top-1/2 -translate-y-1/2 z-10">
        {FLOATING_PROPERTIES.map((property, index) => (
          <div
            key={index}
            className={`group cursor-pointer transform transition-all duration-500 hover:scale-110 hover:z-20
              ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}
              ${index === 0 ? 'rotate-[3deg] hover:rotate-0' : ''}
              ${index === 1 ? 'rotate-[-2deg] hover:rotate-0' : ''}
              ${index === 2 ? 'rotate-[4deg] hover:rotate-0' : ''}
            `}
            style={{ transitionDelay: `${800 + index * 200}ms` }}
            onClick={() => navigate(`/listing/${property.id}`)}
          >
            <div className="w-36 2xl:w-40 bg-white/15 backdrop-blur-xl rounded-lg overflow-hidden border border-white/25 shadow-2xl">
              <div className="aspect-square overflow-hidden">
                <img
                  src={property.image}
                  alt={property.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="px-2.5 py-2">
                <p className="text-white font-medium text-xs truncate">{property.name}</p>
                <p className="text-white/70 text-[11px]">${property.price}/night</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 pb-20">
        <div className="w-full max-w-4xl mx-auto text-center">
          {/* Animated Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6 transform transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-white/90 text-sm font-medium">100+ Verified Properties in Lebanon</span>
          </div>

          {/* Main Heading with Gradient */}
          <h1
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight transform transition-all duration-700 delay-100 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="text-white">Stay anywhere</span>
            <br />
            <span className="bg-gradient-to-r from-primary-300 via-white to-primary-300 bg-clip-text text-transparent">
              in Lebanon
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transform transition-all duration-700 delay-200 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Ski Faraya in the morning, swim in Batroun by sunset. Book the trip,
            then go.
          </p>

          {/* Search Bar */}
          <div
            className={`max-w-xl mx-auto mb-8 transform transition-all duration-700 delay-300 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const input = (e.target as HTMLFormElement).elements.namedItem('heroSearch') as HTMLInputElement;
                const val = input?.value?.trim();
                navigate(val ? `/search?location=${encodeURIComponent(val)}` : '/search');
              }}
              className="flex items-center bg-white/15 backdrop-blur-xl border border-white/30 rounded-full overflow-hidden shadow-2xl hover:bg-white/20 transition-all"
            >
              <div className="flex items-center flex-1 px-5 py-4">
                <svg className="w-5 h-5 text-white/70 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  name="heroSearch"
                  type="text"
                  placeholder="Where in Lebanon are you going?"
                  className="flex-1 bg-transparent outline-none text-white placeholder-white/60 text-base"
                />
              </div>
              <button
                type="submit"
                className="bg-primary-500 text-white px-6 py-3 m-1.5 rounded-full hover:bg-primary-600 transition-colors font-medium text-sm"
              >
                Search
              </button>
            </form>
          </div>

          {/* Quick Links */}
          <div
            className={`transform transition-all duration-700 delay-400 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex flex-wrap justify-center gap-3">
              {['Batroun', 'Byblos', 'Faraya', 'Ehden'].map((location, i) => (
                <button
                  key={location}
                  onClick={() => navigate(`/search?location=${location}`)}
                  className={`px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 text-sm hover:bg-white/20 hover:text-white transition-all transform ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${400 + i * 100}ms` }}
                >
                  {location}
                </button>
              ))}
            </div>
          </div>

          {/* Stats Row */}
          <div
            className={`mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto transform transition-all duration-700 delay-500 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Stat value="100+" label="Properties" />
            <Stat value="25+" label="Locations" />
            <Stat value="4.8" label="Avg Rating" icon={<StarIcon />} />
          </div>
        </div>
      </div>

      {/* Bottom Wave SVG */}
      <div className="absolute bottom-0 left-0 right-0 z-0 pointer-events-none">
        <svg viewBox="0 0 1440 60" fill="none" className="w-full">
          <path
            d="M0 60V30C240 10 480 0 720 0C960 0 1200 10 1440 30V60H0Z"
            fill="white"
          />
        </svg>
      </div>

      {/* Custom Animations CSS */}
      <style>{`
        @keyframes slow-zoom {
          0%, 100% { transform: scale(1.05); }
          50% { transform: scale(1.1); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.2); }
        }
        .animate-slow-zoom {
          animation: slow-zoom 20s ease-in-out infinite;
        }
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }

      `}</style>
    </section>
  );
};

// Stat Component
const Stat: React.FC<{ value: string; label: string; icon?: React.ReactNode }> = ({ value, label, icon }) => (
  <div className="text-center">
    <div className="flex items-center justify-center gap-1">
      <span className="text-2xl md:text-3xl font-bold text-white">{value}</span>
      {icon}
    </div>
    <span className="text-white/60 text-sm">{label}</span>
  </div>
);

// Icons
const StarIcon: React.FC = () => (
  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

export default HeroSection;
